<template>
  <div class="tc-page">
    <!-- ─────────── HEADER ─────────── -->
    <header class="tc-header">
      <div class="tc-header__inner">
        <button type="button" class="tc-back" @click="$router.go(-1)" aria-label="חזרה">
          <v-icon size="20" color="#fff">mdi-arrow-right</v-icon>
        </button>

        <div class="tc-header__main">
          <div class="tc-header__title-row">
            <span class="tc-header__id">#{{ ticket?.integer_id }}</span>
            <h1 class="tc-header__title">פרטי פנייה</h1>
          </div>
          <div class="tc-header__meta" v-if="ticket">
            <v-icon size="13">mdi-clock-outline</v-icon>
            <span>נוצר ב־{{ new Date(ticket.createdAt).toLocaleString('he-IL') }}</span>
          </div>
        </div>

        <span v-if="ticket" class="tc-status-pill"
          :class="ticket.status === 'close' ? 'tc-status-pill--closed' : 'tc-status-pill--open'">
          <v-icon size="13">{{ ticket.status === 'close' ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline'
          }}</v-icon>
          {{ ticket.status === 'close' ? 'סגור' : 'פתוח' }}
        </span>
      </div>
    </header>

    <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

    <main v-if="ticket" class="tc-main">

      <!-- SUBJECT CARD -->
      <section class="tc-subject">
        <div class="tc-subject__bar" aria-hidden="true"></div>
        <div class="tc-subject__body">
          <span class="tc-subject__eyebrow">
            <v-icon size="13">mdi-bookmark-outline</v-icon>
            נושא הפנייה
          </span>
          <h2 class="tc-subject__title">{{ ticket.subject }}</h2>
        </div>
      </section>

      <!-- MESSAGES TIMELINE -->
      <section class="tc-timeline">
        <div class="tc-timeline__head">
          <v-icon size="16">mdi-message-text-outline</v-icon>
          <span>היסטוריית שיחה</span>
          <span class="tc-timeline__count">{{ allMessages.length.toLocaleString('he-IL') }}</span>
        </div>

        <div v-for="(message, index) in allMessages" :key="message._id || index" class="tc-msg"
          :class="{ 'tc-msg--mine': message.responder_id.role === role }">

          <div class="tc-msg__row">
            <!-- avatar -->
            <div class="tc-msg__avatar"
              :class="message.responder_id.role === role ? 'tc-msg__avatar--mine' : 'tc-msg__avatar--other'">
              {{ (message.responder_id.name || '?').charAt(0).toUpperCase() }}
            </div>

            <!-- bubble + meta -->
            <div class="tc-msg__bubble-wrap">
              <div class="tc-msg__meta">
                <span class="tc-msg__sender">{{ message.responder_id.name }}</span>
                <span class="tc-msg__sep">·</span>
                <span class="tc-msg__time">{{ formatTime(message.createdAt) }}</span>
              </div>

              <div class="tc-msg__bubble">
                <div v-if="formatMessage(message.message)" class="tc-msg__content"
                  v-html="formatMessage(message.message)"></div>

                <!-- File attachments -->
                <div v-if="message.type === 'file'" class="tc-files"
                  :class="{ 'tc-files--standalone': !formatMessage(message.message) }">
                  <div v-for="(fileUrl, idx) in extractFileUrls(message.message)" :key="idx" class="tc-file"
                    @click="openFile(fileUrl)">
                    <div class="tc-file__main">
                      <div class="tc-file__icon" :class="`tc-file__icon--${getFileExtension(fileUrl)}`">
                        <v-icon color="#fff" size="20">{{ getFileIconFromUrl(fileUrl) }}</v-icon>
                      </div>
                      <div class="tc-file__info">
                        <span class="tc-file__name">{{ getFileName(fileUrl) }}</span>
                        <span class="tc-file__ext">{{ getFileExtension(fileUrl).toUpperCase() }}</span>
                      </div>
                      <button type="button" class="tc-file__dl" @click.stop="downloadFile(fileUrl)"
                        aria-label="הורד קובץ">
                        <v-icon size="17">mdi-download</v-icon>
                      </button>
                    </div>
                    <div v-if="isImageFile(fileUrl)" class="tc-file__preview">
                      <v-img :src="fileUrl" max-height="200" contain></v-img>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- REPLY BOX -->
      <div v-if="ticket.status === 'open'" class="tc-reply" :class="{ 'tc-reply--collapsed': isScrolling }"
        @mouseenter="isHoveringReply = true" @mouseleave="isHoveringReply = false">

        <div class="tc-reply__inner">
          <v-textarea v-model="reply" outlined auto-grow rows="2" hide-details placeholder="כתוב את תגובתך כאן…"
            class="tc-reply__textarea"></v-textarea>

          <!-- File chip -->
          <div v-if="uploadedFile" class="tc-reply__file">
            <div class="tc-reply__file-icon">
              <v-icon size="16" color="#0d2c6d">{{ getFileIcon(uploadedFile.type) }}</v-icon>
            </div>
            <span class="tc-reply__file-name">{{ uploadedFile.name }}</span>
            <button type="button" class="tc-reply__file-remove" @click="removeUploadedFile" aria-label="הסר קובץ">
              <v-icon size="14">mdi-close</v-icon>
            </button>
          </div>

          <div class="tc-reply__actions">
            <div class="tc-reply__actions-left">
              <input ref="fileInput" type="file" style="display: none" @change="handleFileSelect"
                :disabled="uploadingFile" />

              <button type="button" class="tc-reply__attach" @click="$refs.fileInput.click()" :disabled="uploadingFile">
                <v-progress-circular v-if="uploadingFile" indeterminate size="16" width="2" color="#475569" />
                <v-icon v-else size="17">mdi-paperclip</v-icon>
                <span>{{ uploadingFile ? 'מעלה…' : 'צרף קובץ' }}</span>
              </button>

              <button type="button" class="tc-reply__send" :disabled="!canSendMessage || progressShow"
                @click="handleReplySubmit">
                <v-progress-circular v-if="progressShow" indeterminate size="16" width="2.2" color="#fff" />
                <template v-else>
                  <v-icon size="17" color="#fff">mdi-send</v-icon>
                  <span>שלח תגובה</span>
                </template>
              </button>
            </div>

            <button v-if="isOwner" type="button" class="tc-reply__close-ticket" @click="openAlertDialog(ticket)"
              :disabled="progressShow">
              <v-icon size="15">mdi-close-circle-outline</v-icon>
              <span>סגור פנייה</span>
            </button>
          </div>
        </div>
      </div>

      <!-- CLOSED BANNER (when ticket is closed) -->
      <div v-else class="tc-closed-banner">
        <v-icon size="20">mdi-lock-outline</v-icon>
        <span>הפנייה סגורה — לא ניתן להוסיף תגובות נוספות</span>
      </div>

      <!-- CLOSE TICKET DIALOG -->
      <v-dialog v-model="alertDialog" max-width="460" content-class="tc-alert" overlay-color="#0d2c6d"
        overlay-opacity="0.55" transition="dialog-bottom-transition" :persistent="progressDialog">
        <v-card class="tc-alert-card">
          <!-- header -->
          <div class="tc-alert-head">
            <div class="tc-alert-head__icon">
              <v-icon color="#fff" size="22">mdi-alert-octagon-outline</v-icon>
            </div>
            <div class="tc-alert-head__text">
              <span class="tc-alert-eyebrow">פעולה בלתי הפיכה</span>
              <h2 class="tc-alert-title">סגירת פנייה</h2>
            </div>
          </div>

          <div class="tc-alert-body">
            <p class="tc-alert-msg">
              האם אתה בטוח שברצונך לסגור את הפנייה?<br>
              <span class="tc-alert-msg__warn">לא ניתן יהיה להוסיף תגובות נוספות לאחר הסגירה.</span>
            </p>

            <div v-if="ticketToClose" class="tc-alert-target">
              <span class="tc-alert-target__id">#{{ ticketToClose.integer_id }}</span>
              <span class="tc-alert-target__subject">{{ ticketToClose.subject }}</span>
            </div>
          </div>

          <div class="tc-alert-foot">
            <v-progress-linear v-if="progressDialog" indeterminate color="#c74747" height="2" />
            <div class="tc-alert-foot__actions">
              <button type="button" class="tc-alert-btn tc-alert-btn--ghost" @click="alertDialog = false"
                :disabled="progressDialog">ביטול</button>
              <button type="button" class="tc-alert-btn tc-alert-btn--danger" @click="handleCloseTicket(ticketToClose)"
                :disabled="progressDialog">
                <v-icon v-if="!progressDialog" size="16" color="#fff">mdi-lock-outline</v-icon>
                <span>{{ progressDialog ? 'סוגר…' : 'כן, סגור פנייה' }}</span>
              </button>
            </div>
          </div>
        </v-card>
      </v-dialog>
    </main>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </div>
</template>

<script>
import Auth from '@/services/auth.service.js'
import ProgressShow from '@/components/widgets/progressShow.vue'
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import { mapState } from 'vuex'

export default {
  name: 'TicketComp',
  components: {
    ProgressShow,
    SnackBar
  },
  data() {
    return {
      progressShow: false,
      progressDialog: false,
      snackbar: false,
      snackbarColorBt: "success",
      snacbarText: "",
      id: this.$route.params.id,
      reply: '',
      allMessages: [],
      ticket: null,
      alertDialog: false,
      ticketToClose: null,
      role: Auth.getUserRole(),
      uploadingFile: false,
      uploadedFile: null,
      isScrolling: false,
      isHoveringReply: false,
      scrollTimeout: null,
    }
  },
  computed: {
    isOwner() {
      return this.ticket?.email === Auth.getUserEmail()
    },
    canSendMessage() {
      return (this.reply.trim() || this.uploadedFile) && !this.uploadingFile
    },
    ...mapState({
      newResponse: state => state.newResponse,
      ticketId: state => state.ticketId,
    }),
  },
  methods: {
    async getTicketMessagesList() {
      try {
        this.progressShow = true
        let token = localStorage.getItem("token")
        const params = { id: this.id }
        let api = `${process.env.VUE_APP_BASE_URL}/tickets/get_ticket_messages?ticketId=${this.id}`
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token))
        const jsonObject = await res.json()

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "error")
        } else if (res.status === 200) {
          this.ticket = jsonObject
          this.allMessages = JSON.parse(JSON.stringify(jsonObject.responses))
          this.handleMarkAsRead(jsonObject, params)
        }
      } catch (error) {
        this.showSnackBar("שגיאה בקבלת נתונים: " + error, "error")
      } finally {
        this.progressShow = false
      }
    },
    async handleMarkAsRead(ticketArg, params) {
      try {
        const responses = ticketArg.responses
        const lastResponse = responses[responses.length - 1]
        if (!lastResponse.read_at) {
          let token = localStorage.getItem("token")
          let api = `${process.env.VUE_APP_BASE_URL}/tickets/mark_as_read?ticketId=${params.id}`
          await fetch(api, ApiServices.requestOptions("GET", "", token))
        }
      } catch (error) {
        console.error("Error marking as read:", error)
      }
    },
    formatMessage(message) {
      // Remove file URLs from text display (they'll be shown as file components)
      const urlRegex = /https?:\/\/[^\s]+/g
      const textWithoutUrls = message.replace(urlRegex, '').trim()

      if (!textWithoutUrls) return ''

      return textWithoutUrls.split('\n')
        .filter(line => line.trim())
        .map(line => `<p class="mb-1">${line}</p>`)
        .join('')
    },
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'עכשיו'
      if (diffMins < 60) return `לפני ${diffMins} דקות`
      if (diffHours < 24) return `לפני ${diffHours} שעות`
      if (diffDays < 7) return `לפני ${diffDays} ימים`

      return date.toLocaleDateString('he-IL')
    },
    hasFileAttachment(message) {
      const urlRegex = /https?:\/\/[^\s]+/g
      return urlRegex.test(message)
    },
    extractFileUrls(message) {
      const urlRegex = /https?:\/\/[^\s]+/g
      return message.match(urlRegex) || []
    },
    getFileIcon(fileType) {
      if (fileType.startsWith('image/')) return 'mdi-image'
      if (fileType === 'application/pdf') return 'mdi-file-pdf-box'
      if (fileType.includes('word')) return 'mdi-file-word'
      if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'mdi-file-excel'
      if (fileType.includes('zip') || fileType.includes('compressed')) return 'mdi-folder-zip'
      if (fileType.startsWith('video/')) return 'mdi-video'
      if (fileType.startsWith('audio/')) return 'mdi-music'
      return 'mdi-file-document'
    },
    async handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.showSnackBar('הקובץ גדול מדי. מקסימום 10MB', 'error')
        return
      }

      try {
        this.uploadingFile = true

        // Create FormData for file upload
        const formData = new FormData()
        formData.append('file', file)
        formData.append('ticketId', this.id)

        let token = localStorage.getItem("token")
        let api = `${process.env.VUE_APP_BASE_URL}/tickets/upload_file`

        const res = await fetch(api, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        })

        const jsonObject = await res.json()

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בהעלאת הקובץ", "error")
        } else if (res.status === 200) {
          this.uploadedFile = {
            name: file.name,
            type: file.type,
            url: jsonObject.file.url,
            filename: jsonObject.file.filename
          }
          this.showSnackBar('הקובץ הועלה בהצלחה', 'success')
        }
      } catch (error) {
        this.showSnackBar(`שגיאה בהעלאת הקובץ: ${error}`, 'error')
      } finally {
        this.uploadingFile = false
        // Reset file input
        this.$refs.fileInput.value = ''
      }
    },
    async removeUploadedFile() {
      if (!this.uploadedFile) return

      try {
        let token = localStorage.getItem("token")
        const filename = this.uploadedFile.filename || this.getFileName(this.uploadedFile.url)
        let api = `${process.env.VUE_APP_BASE_URL}/tickets/delete_file/${filename}`

        const res = await fetch(api, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (res.status >= 400) {
          this.showSnackBar("שגיאה במחיקת הקובץ מהשרת", "error")
        } else if (res.status === 200) {
          this.uploadedFile = null
          this.showSnackBar('הקובץ נמחק בהצלחה', 'success')
        }
      } catch (error) {
        console.error('Error deleting file:', error)
        this.showSnackBar(`שגיאה במחיקת הקובץ: ${error}`, 'error')
        // Remove from UI even if server deletion fails
        this.uploadedFile = null
      }
    },
    async handleReplySubmit() {
      if (!this.canSendMessage) return

      try {
        this.progressShow = true
        let token = localStorage.getItem("token")
        let api = `${process.env.VUE_APP_BASE_URL}/tickets/reply_message`

        // Construct message with file URL if exists
        let messageText = this.reply.trim()
        if (this.uploadedFile) {
          messageText += (messageText ? '\n' : '') + this.uploadedFile.url
        }

        const myJson = JSON.stringify({
          ticketId: this.id,
          newMessage: messageText,
          messageType: this.uploadedFile ? 'file' : 'text'

        })

        const res = await fetch(api, ApiServices.requestOptions("POST", myJson, token))
        const jsonObject = await res.json()

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בשליחת התגובה", "error")
        } else if (res.status === 200) {
          this.allMessages.push(jsonObject)
          this.reply = ''
          this.uploadedFile = null
          this.showSnackBar('התגובה נשלחה בהצלחה', 'success')

          // Scroll to bottom
          this.$nextTick(() => {
            const timeline = document.querySelector('.messages-timeline')
            if (timeline) {
              timeline.scrollTop = timeline.scrollHeight
            }
          })
        }
      } catch (error) {
        this.showSnackBar(`שגיאה בשליחת התגובה: ${error}`, 'error')
      } finally {
        this.progressShow = false
      }
    },
    openAlertDialog(ticket) {
      this.alertDialog = true
      this.ticketToClose = ticket
    },
    async handleCloseTicket(ticket) {
      try {
        this.progressDialog = true
        let token = localStorage.getItem("token")
        let api = `${process.env.VUE_APP_BASE_URL}/tickets/close_ticket?ticketId=${ticket._id}`
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token))

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בסגירת הפנייה", "error")
        } else if (res.status === 200) {
          this.ticket.status = 'close'
          this.alertDialog = false
          this.showSnackBar('הפנייה נסגרה בהצלחה', 'success')
        }
      } catch (error) {
        this.showSnackBar(`שגיאה בסגירת הפנייה: ${error}`, 'error')
      } finally {
        this.progressDialog = false
      }
    },
    showSnackBar(message, color) {
      this.snacbarText = message
      this.snackbarColorBt = color
      this.snackbar = true
    },

    getFileName(fileUrl) {
      try {
        const url = new URL(fileUrl)
        return decodeURIComponent(url.pathname.split('/').pop())
      } catch {
        return 'קובץ מצורף'
      }
    },

    getFileExtension(fileUrl) {
      const fileName = this.getFileName(fileUrl)
      return fileName.split('.').pop().toLowerCase()
    },

    getFileIconFromUrl(fileUrl) {
      const ext = this.getFileExtension(fileUrl)
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'mdi-image'
      if (['pdf'].includes(ext)) return 'mdi-file-pdf-box'
      if (['doc', 'docx'].includes(ext)) return 'mdi-file-word'
      if (['xls', 'xlsx'].includes(ext)) return 'mdi-file-excel'
      if (['zip', 'rar', '7z'].includes(ext)) return 'mdi-folder-zip'
      if (['mp4', 'avi', 'mov'].includes(ext)) return 'mdi-video'
      if (['mp3', 'wav', 'ogg'].includes(ext)) return 'mdi-music'
      return 'mdi-file-document'
    },

    getFileColor(fileUrl) {
      const ext = this.getFileExtension(fileUrl)
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'blue'
      if (['pdf'].includes(ext)) return 'red'
      if (['doc', 'docx'].includes(ext)) return 'blue darken-2'
      if (['xls', 'xlsx'].includes(ext)) return 'green'
      if (['zip', 'rar', '7z'].includes(ext)) return 'orange'
      if (['mp4', 'avi', 'mov'].includes(ext)) return 'purple'
      if (['mp3', 'wav', 'ogg'].includes(ext)) return 'pink'
      return 'grey'
    },

    getFileColorClass(fileUrl) {
      const ext = this.getFileExtension(fileUrl)
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'blue lighten-5'
      if (['pdf'].includes(ext)) return 'red lighten-5'
      if (['doc', 'docx'].includes(ext)) return 'blue lighten-4'
      if (['xls', 'xlsx'].includes(ext)) return 'green lighten-5'
      if (['zip', 'rar', '7z'].includes(ext)) return 'orange lighten-5'
      if (['mp4', 'avi', 'mov'].includes(ext)) return 'purple lighten-5'
      if (['mp3', 'wav', 'ogg'].includes(ext)) return 'pink lighten-5'
      return 'grey lighten-4'
    },

    isImageFile(fileUrl) {
      const ext = this.getFileExtension(fileUrl)
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
    },

    openFile(fileUrl) {
      window.open(fileUrl + `?token=${localStorage.getItem('token')}`, '_blank')
    },

    downloadFile(fileUrl) {
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = this.getFileName(fileUrl)
      link.click()
    },

    handleScroll() {
      // Don't collapse if hovering over reply section
      if (this.isHoveringReply) return

      this.isScrolling = true

      // Clear existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }

      // Keep collapsed for a bit after scrolling stops
      this.scrollTimeout = setTimeout(() => {
        if (!this.isHoveringReply) {
          this.isScrolling = false
        }
      }, 1000)
    },
  },
  watch: {
    newResponse(newValue) {
      if (this.ticket._id === this.ticketId) {
        this.allMessages.push(newValue)
        this.ticket.responses.push(newValue)
      }
    },
    isHoveringReply(newValue) {
      if (newValue) {
        this.isScrolling = false
      }
    }
  },
  mounted() {
    this.getTicketMessagesList()
    // Wire handleScroll to window scroll so the reply box collapses
    // while the page scrolls and re-expands on hover.
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout)
  }
}
</script>

<style scoped>
/* ─────────────────── TOKENS ─────────────────── */
.tc-page {
  --c-navy: #0d2c6d;
  --c-navy-soft: #142f78;
  --c-navy-deep: #081f54;
  --c-bubble: #4f5fd0;
  --c-bubble-deep: #6366f1;
  --c-indigo: #3F51B5;
  --c-indigo-soft: rgba(63, 81, 181, 0.10);
  --c-purple: #8066f0;
  --c-gold: #fdb721;
  --c-gold-deep: #d99b08;
  --c-gold-soft: rgba(253, 183, 33, 0.10);
  --c-bg: #f3f6f9;
  --c-surface: #ffffff;
  --c-surface-2: #fbfcfe;
  --c-border: #e3e8ef;
  --c-border-soft: #eef2f7;
  --c-text: #0f172a;
  --c-text-soft: #475569;
  --c-text-mute: #94a3b8;
  --c-green: #16804b;
  --c-green-soft: rgba(22, 128, 75, 0.10);
  --c-red: #c74747;
  --c-red-deep: #a93636;

  --c-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
  --c-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
  --c-shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
  --c-ease: cubic-bezier(0.22, 1, 0.36, 1);

  min-height: 100vh;
  padding-bottom: 240px;
  background: var(--c-bg);
  direction: rtl;
  font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────────── HEADER ─────────────────── */
.tc-header {
  background:
    radial-gradient(800px 200px at 90% -30%, rgba(253, 183, 33, 0.18), transparent 60%),
    linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
  color: #fff;
  padding: 12px 0 14px;
  box-shadow: var(--c-shadow-md);
}

.tc-header__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 22px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.tc-back {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.20);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.10);
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.tc-back:hover {
  background: rgba(255, 255, 255, 0.18);
  transform: translateX(2px);
}

.tc-back ::v-deep .v-icon {
  color: #fff !important;
}

.tc-header__main {
  flex: 1;
  min-width: 0;
}

.tc-header__title-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.tc-header__id {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 6px;
  background: rgba(253, 183, 33, 0.22);
  border: 1px solid rgba(253, 183, 33, 0.35);
  color: var(--c-gold);
  font-weight: 800;
  font-size: 0.82rem;
  letter-spacing: 0.02em;
  direction: ltr;
}

.tc-header__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.tc-header__meta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 600;
}

.tc-header__meta ::v-deep .v-icon {
  color: rgba(255, 255, 255, 0.55) !important;
}

.tc-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.tc-status-pill ::v-deep .v-icon {
  color: inherit !important;
}

.tc-status-pill--open {
  color: #62e8a1;
  background: rgba(22, 163, 74, 0.18);
  border: 1px solid rgba(98, 232, 161, 0.40);
}

.tc-status-pill--closed {
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

/* ─────────────────── MAIN ─────────────────── */
.tc-main {
  max-width: 1200px;
  margin: 18px auto 0;
  padding: 0 22px;
}

/* ─────────────────── SUBJECT CARD ─────────────────── */
.tc-subject {
  display: flex;
  background: var(--c-surface);
  border: 1px solid var(--c-border-soft);
  border-radius: 14px;
  box-shadow: var(--c-shadow-md);
  overflow: hidden;
  margin-bottom: 18px;
}

.tc-subject__bar {
  flex-shrink: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--c-navy) 0%, var(--c-gold) 100%);
}

.tc-subject__body {
  padding: 14px 18px;
  flex: 1;
  min-width: 0;
}

.tc-subject__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-text-mute);
  margin-bottom: 5px;
}

.tc-subject__eyebrow ::v-deep .v-icon {
  color: var(--c-navy) !important;
}

.tc-subject__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--c-text);
  letter-spacing: -0.01em;
  line-height: 1.4;
}

/* ─────────────────── TIMELINE ─────────────────── */
.tc-timeline {
  background: var(--c-surface);
  border: 1px solid var(--c-border-soft);
  border-radius: 14px;
  padding: 18px 22px 22px;
  box-shadow: var(--c-shadow-md);
}

.tc-timeline__head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 14px;
  margin-bottom: 16px;
  border-bottom: 1px dashed var(--c-border);
  font-weight: 700;
  color: var(--c-navy);
  font-size: 0.95rem;
}

.tc-timeline__head ::v-deep .v-icon {
  color: var(--c-navy) !important;
}

.tc-timeline__count {
  margin-right: auto;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--c-indigo-soft);
  color: var(--c-indigo);
  font-size: 0.78rem;
  font-weight: 800;
  font-feature-settings: "tnum" 1;
}

/* ─────── MESSAGES ─────── */
.tc-msg {
  margin-bottom: 18px;
  animation: tc-slideIn 0.35s var(--c-ease);
}

.tc-msg:last-child {
  margin-bottom: 0;
}

@keyframes tc-slideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tc-msg__row {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  max-width: 78%;
}

/* my messages → flipped to RTL right side */
.tc-msg--mine .tc-msg__row {
  flex-direction: row-reverse;
  margin-right: auto;
  margin-left: 0;
}

.tc-msg:not(.tc-msg--mine) .tc-msg__row {
  margin-left: auto;
  margin-right: 0;
}

.tc-msg__avatar {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 800;
  font-size: 0.9rem;
  user-select: none;
  box-shadow: 0 2px 6px -2px rgba(13, 44, 109, 0.30);
}

.tc-msg__avatar--mine {
 background: linear-gradient(135deg, #0d2c6d 0%, #2456b8 100%);
}

.tc-msg__avatar--other {
  background: linear-gradient(135deg, var(--c-indigo) 0%, #5263c4 100%);
}

.tc-msg__bubble-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tc-msg__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 0 3px;
  font-size: 0.78rem;
  color: var(--c-text-mute);
  font-weight: 600;
}

.tc-msg--mine .tc-msg__meta {
  justify-content: flex-end;
}

.tc-msg__sender {
  color: var(--c-text-soft);
  font-weight: 700;
}

.tc-msg__sep {
  opacity: 0.5;
}

.tc-msg__time {
  color: var(--c-text-mute);
}

/* bubbles */
.tc-msg__bubble {
  padding: 11px 14px;
  border-radius: 14px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  box-shadow: var(--c-shadow-sm);
  transition: box-shadow 0.2s var(--c-ease);
  /* Size bubble to its content, capped by row's 78% max-width */
  width: fit-content;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.tc-msg__bubble:hover {
  box-shadow: var(--c-shadow-md);
}

/* My bubble: filled navy with white text */
.tc-msg--mine .tc-msg__bubble {
  background: linear-gradient(135deg, #0d2c6d 0%, #2456b8 100%);
  box-shadow: 0 6px 16px -4px rgba(13, 44, 109, 0.32);
  border-color: transparent;
  color: #fff;
  /* Tail-style corner — bottom-right less rounded (toward avatar) */
  border-bottom-right-radius: 4px;
}

.tc-msg--mine .tc-msg__content {
  color: #fff;
}

.tc-msg:not(.tc-msg--mine) .tc-msg__bubble {
  /* Other bubble: white with tail bottom-left (toward avatar) */
  border-bottom-left-radius: 4px;
}

.tc-msg__content {
  font-size: 0.93rem;
  line-height: 1.55;
  color: var(--c-text);
  word-wrap: break-word;
}

.tc-msg__content ::v-deep p {
  margin: 0 0 4px;
}

.tc-msg__content ::v-deep p:last-child {
  margin-bottom: 0;
}

/* ─────────────────── FILE CARDS ─────────────────── */
.tc-files {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tc-msg--mine .tc-files {
  border-top-color: rgba(255, 255, 255, 0.18);
}

.tc-files--standalone {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.tc-file {
  background: var(--c-surface);
  border: 1px solid var(--c-border-soft);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.tc-msg--mine .tc-file {
  background: rgba(255, 255, 255, 0.10);
  border-color: rgba(255, 255, 255, 0.18);
}

.tc-file:hover {
  transform: translateY(-1px);
  box-shadow: var(--c-shadow-md);
}

.tc-msg--mine .tc-file:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.30);
}

.tc-file__main {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
}

.tc-file__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--c-text-mute);
}

.tc-file__icon--jpg,
.tc-file__icon--jpeg,
.tc-file__icon--png,
.tc-file__icon--gif,
.tc-file__icon--webp,
.tc-file__icon--svg {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.tc-file__icon--pdf {
  background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
}

.tc-file__icon--doc,
.tc-file__icon--docx {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.tc-file__icon--xls,
.tc-file__icon--xlsx {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
}

.tc-file__icon--zip,
.tc-file__icon--rar,
.tc-file__icon--7z {
  background: linear-gradient(135deg, #f97316 0%, #c2410c 100%);
}

.tc-file__icon--mp4,
.tc-file__icon--avi,
.tc-file__icon--mov {
  background: linear-gradient(135deg, var(--c-purple) 0%, #6b54e0 100%);
}

.tc-file__icon--mp3,
.tc-file__icon--wav,
.tc-file__icon--ogg {
  background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
}

.tc-file__icon ::v-deep .v-icon {
  color: #fff !important;
}

.tc-file__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.tc-file__name {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--c-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
}

.tc-msg--mine .tc-file__name {
  color: #fff;
}

.tc-file__ext {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--c-text-mute);
  letter-spacing: 0.05em;
}

.tc-msg--mine .tc-file__ext {
  color: rgba(255, 255, 255, 0.6);
}

.tc-file__dl {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  background: var(--c-surface);
  color: var(--c-text-soft);
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
  flex-shrink: 0;
}

.tc-file__dl:hover {
  border-color: transparent;
  background: var(--c-navy);
  color: #fff;
  transform: translateY(-1px);
}

.tc-file__dl ::v-deep .v-icon {
  color: inherit !important;
}

.tc-msg--mine .tc-file__dl {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.tc-msg--mine .tc-file__dl:hover {
  background: var(--c-gold);
  color: #2d1f00;
}

.tc-file__preview {
  border-top: 1px solid var(--c-border-soft);
  background: #fafafa;
}

/* ─────────────────── REPLY BOX (sticky bottom) ─────────────────── */
.tc-reply {
  position: fixed;
  bottom: 18px;
  right: 50%;
  transform: translateX(50%);
  width: calc(100% - 44px);
  max-width: 1156px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 16px;
  box-shadow: 0 20px 50px -10px rgba(13, 44, 109, 0.20), 0 6px 14px -4px rgba(13, 44, 109, 0.10);
  z-index: 10;
  overflow: hidden;
  transition: all 0.3s var(--c-ease);
}

.tc-reply::before {
  /* gold top accent */
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--c-navy) 0%, var(--c-gold) 100%);
}

.tc-reply--collapsed {
  max-height: 56px;
  overflow: hidden;
  box-shadow: 0 6px 18px -8px rgba(13, 44, 109, 0.15);
}

.tc-reply--collapsed .tc-reply__inner {
  opacity: 0.55;
  pointer-events: none;
}

.tc-reply__inner {
  padding: 14px 16px;
  transition: opacity 0.25s var(--c-ease);
}

.tc-reply__textarea {
  margin-bottom: 10px;
}

::v-deep .tc-reply__textarea .v-input__slot {
  background: var(--c-surface-2) !important;
  border-radius: 11px !important;
}

::v-deep .tc-reply__textarea fieldset {
  border-color: var(--c-border) !important;
  transition: all 0.2s var(--c-ease);
}

::v-deep .tc-reply__textarea:hover fieldset {
  border-color: #c7d2e0 !important;
}

::v-deep .tc-reply__textarea.v-input--is-focused fieldset {
  border-color: var(--c-navy) !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.08);
}

::v-deep .tc-reply__textarea textarea {
  font-family: inherit;
  font-size: 0.93rem;
  line-height: 1.5;
}

/* Uploaded file chip */
.tc-reply__file {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding: 6px 6px 6px 12px;
  border-radius: 999px;
  background: rgba(13, 44, 109, 0.06);
  border: 1px solid rgba(13, 44, 109, 0.14);
}

.tc-reply__file-icon {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(13, 44, 109, 0.12);
}

.tc-reply__file-name {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--c-navy);
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tc-reply__file-remove {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: rgba(13, 44, 109, 0.10);
  color: var(--c-navy);
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.tc-reply__file-remove:hover {
  background: var(--c-red);
  color: #fff;
}

.tc-reply__file-remove ::v-deep .v-icon {
  color: inherit !important;
}

/* Actions row */
.tc-reply__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.tc-reply__actions-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tc-reply__attach {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--c-border);
  border-radius: 10px;
  background: var(--c-surface);
  color: var(--c-text-soft);
  font-family: inherit;
  font-weight: 700;
  font-size: 0.86rem;
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.tc-reply__attach:hover:not(:disabled) {
  border-color: var(--c-navy);
  color: var(--c-navy);
  background: rgba(13, 44, 109, 0.04);
}

.tc-reply__attach:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tc-reply__attach ::v-deep .v-icon {
  color: inherit !important;
}

.tc-reply__send {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--c-navy) 0%, var(--c-navy-soft) 100%);
  color: #fff;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
  box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.45);
}

.tc-reply__send:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.08);
}

.tc-reply__send:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.tc-reply__send ::v-deep .v-icon {
  color: #fff !important;
}

.tc-reply__close-ticket {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border: 1px solid rgba(199, 71, 71, 0.30);
  border-radius: 10px;
  background: transparent;
  color: var(--c-red);
  font-family: inherit;
  font-weight: 700;
  font-size: 0.84rem;
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.tc-reply__close-ticket:hover:not(:disabled) {
  background: var(--c-red);
  border-color: var(--c-red);
  color: #fff;
}

.tc-reply__close-ticket ::v-deep .v-icon {
  color: inherit !important;
}

/* ─────────────────── CLOSED BANNER ─────────────────── */
.tc-closed-banner {
  margin-top: 18px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(148, 163, 184, 0.10);
  border: 1px dashed var(--c-border);
  border-radius: 12px;
  color: var(--c-text-soft);
  font-weight: 700;
  font-size: 0.92rem;
  justify-content: center;
}

.tc-closed-banner ::v-deep .v-icon {
  color: var(--c-text-mute) !important;
}

/* ─────────────────── CLOSE-TICKET DIALOG ─────────────────── */
.tc-alert {
  border-radius: 18px !important;
  overflow: hidden;
}

.tc-alert-card {
  font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
  direction: rtl;
  background: #ffffff !important;
  border-radius: 18px !important;
  overflow: hidden;
}

.tc-alert-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  background:
    radial-gradient(500px 160px at 90% -40%, rgba(0, 0, 0, 0.18), transparent 60%),
    linear-gradient(135deg, var(--c-red) 0%, var(--c-red-deep) 100%);
  color: #fff;
}

.tc-alert-head__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: grid;
  place-items: center;
  animation: tc-shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

@keyframes tc-shake {

  10%,
  90% {
    transform: translateX(-1px);
  }

  20%,
  80% {
    transform: translateX(2px);
  }

  30%,
  50%,
  70% {
    transform: translateX(-3px);
  }

  40%,
  60% {
    transform: translateX(3px);
  }
}

.tc-alert-head__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.tc-alert-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.tc-alert-title {
  margin: 1px 0 0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.01em;
}

.tc-alert-body {
  padding: 22px 24px 18px;
  background: var(--c-surface-2);
}

.tc-alert-msg {
  margin: 0 0 14px;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--c-text);
}

.tc-alert-msg__warn {
  color: var(--c-red);
  font-weight: 700;
  font-size: 0.86rem;
}

.tc-alert-target {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--c-surface);
  border: 1px solid var(--c-border-soft);
  border-radius: 11px;
}

.tc-alert-target__id {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 6px;
  background: rgba(13, 44, 109, 0.08);
  color: var(--c-navy);
  font-weight: 800;
  font-size: 0.82rem;
  direction: ltr;
}

.tc-alert-target__subject {
  font-weight: 700;
  color: var(--c-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.92rem;
}

.tc-alert-foot {
  background: var(--c-surface);
  border-top: 1px solid var(--c-border-soft);
}

.tc-alert-foot__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
}

.tc-alert-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 22px;
  border-radius: 10px;
  border: none;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.tc-alert-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tc-alert-btn--ghost {
  background: transparent;
  color: var(--c-text-soft);
  border: 1px solid var(--c-border);
}

.tc-alert-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  color: var(--c-text);
}

.tc-alert-btn--danger {
  background: linear-gradient(135deg, var(--c-red) 0%, var(--c-red-deep) 100%);
  color: #fff;
  box-shadow: 0 6px 14px -4px rgba(199, 71, 71, 0.55);
}

.tc-alert-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.08);
}

.tc-alert-btn ::v-deep .v-icon {
  color: #fff !important;
}

/* ─────────────────── RESPONSIVE ─────────────────── */
@media (max-width: 900px) {
  .tc-header__inner {
    padding: 0 14px;
  }

  .tc-main {
    padding: 0 12px;
    margin-top: 14px;
  }

  .tc-msg__row {
    max-width: 90%;
  }

  .tc-reply {
    width: calc(100% - 24px);
    bottom: 12px;
  }

  .tc-page {
    padding-bottom: 280px;
  }
}

@media (max-width: 600px) {
  .tc-header__title {
    font-size: 1rem;
  }

  .tc-status-pill {
    padding: 4px 9px;
    font-size: 0.72rem;
  }

  .tc-msg__row {
    max-width: 95%;
  }

  .tc-msg__avatar {
    width: 32px;
    height: 32px;
    font-size: 0.82rem;
  }

  .tc-subject__title {
    font-size: 1rem;
  }

  .tc-reply__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .tc-reply__actions-left {
    flex-wrap: wrap;
  }

  .tc-reply__close-ticket {
    justify-content: center;
  }

  .tc-file__name {
    max-width: 160px;
  }
}

/* Custom scrollbar for the timeline if it scrolls */
.tc-timeline::-webkit-scrollbar {
  width: 8px;
}

.tc-timeline::-webkit-scrollbar-track {
  background: var(--c-border-soft);
  border-radius: 10px;
}

.tc-timeline::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.tc-timeline::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

@media (prefers-reduced-motion: reduce) {

  .tc-msg,
  .tc-alert-head__icon {
    animation: none;
  }
}
</style>